import { IComponentOptions, IController, IPromise, IQService, IRootScopeService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { Unsubscribe } from "redux";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { AssigneeActions } from "@gtmhub/assignees/redux/assignee-actions";
import { toTeamAssigneeIdMap } from "@gtmhub/assignees/redux/assignee-selectors";
import { IModalComponent } from "@gtmhub/core/routing";
import { IEmployee, IEnrichedEmployee } from "@gtmhub/employees/models/models";
import { EmployeeService } from "@gtmhub/employees/services/employee.service";
import { EmployeeEvents } from "@gtmhub/employees/services/events";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { INgRedux } from "@gtmhub/state-management";
import { ITeamsIdMap } from "@gtmhub/teams";
import { UserProfileService } from "@gtmhub/user-profile/user-profile.service";
import { ChangeUserPictureModalService, IUser } from "@gtmhub/users";
import { IPermissionsStoreState, hasPermissions } from "@gtmhub/users/redux";
import { UserService } from "@gtmhub/users/user-service";
import { unique } from "@gtmhub/util";
import { CfMap } from "@webapp/custom-fields/models/custom-fields.models";
import { areCustomFieldsChanged } from "@webapp/custom-fields/utils";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { IProfile } from "@webapp/user-profile/models/user-profile.models";

interface IEmployeeProfileStateParamsService {
  employeeId: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface EmployeeProfileCtrl extends IModalComponent {}
export class EmployeeProfileCtrl implements IController {
  private unsubscribeFromStore: Unsubscribe;

  employee: IEnrichedEmployee;
  teamsIdMap: ITeamsIdMap;
  allTeamIds: string[];
  indicators: {
    loading: boolean;
  };
  isCurrentUserProfile: boolean;
  user: IUser;
  profile: IProfile;

  public favoritesManager: FavoritesManager;

  private events: EmployeeEvents;

  static $inject = [
    "$rootScope",
    "$scope",
    "$q",
    "$stateParams",
    "$state",
    "EmployeeService",
    "UserService",
    "UserProfileService",
    "$ngRedux",
    "AssigneeActions",
    "UIErrorHandlingService",
    "FavoritesManagerFactory",
    "ChangeUserPictureModalService",
  ];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: IModalScope,
    private $q: IQService,
    private $stateParams: IEmployeeProfileStateParamsService,
    private $stateService: StateService,
    private employeeService: EmployeeService,
    private userService: UserService,
    private profileService: UserProfileService,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions,
    private uiErrorHandlingService: UIErrorHandlingService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private changeUserPictureModalService: ChangeUserPictureModalService
  ) {
    this.indicators = {
      loading: true,
    };

    this.favoritesManager = this.favoritesManagerFactory(["user", "team"]);

    this.profile = this.profileService.getProfile();
    this.events = new EmployeeEvents(this.$rootScope);
  }

  $onInit(): IPromise<unknown> {
    this.unsubscribeFromStore = this.$ngRedux.connect(this.bindStateToScope.bind(this))(this);
    if (this.$stateParams.employeeId) {
      this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    }

    this.$scope.$on("employeeChanged", this.getComponentData);
    this.favoritesManager.initFavorites();
    return this.getComponentData();
  }

  $onDestroy(): void {
    this.unsubscribeFromStore();
  }

  handleEmployeeStatusChange(): void {
    this.broadcastEmployeeChanged();
    this.returnToState();
  }

  handleEmployeeDeleted(): void {
    this.broadcastEmployeeDeleted();
    this.returnToState();
  }

  broadcastEmployeeChanged = (): void => {
    this.$rootScope.$broadcast("employeeChanged");
    this.$rootScope.$broadcast("employeesChanged");
  };

  broadcastEmployeeDeleted = (): void => {
    this.$rootScope.$broadcast("userDeleted", { id: this.user.id });
  };

  returnToState = (): void => {
    this.$stateService.go("gtmhub.employees");
  };

  navigateToProfile = (teamId: string) => {
    if (teamId) {
      this.$stateService.go("gtmhub.teams.team", { teamId });
    }
  };

  updateTeams = (teamIds: string[]): void => {
    const oldTeams = new Set(this.employee.teamIds);
    const sameValue = oldTeams.size === teamIds.length && teamIds.every((teamId) => oldTeams.has(teamId));
    if (sameValue) {
      return;
    }

    const employee: Partial<IEmployee> = {
      teamIds: teamIds,
    };

    this.userService.patchUser(this.employee.id, employee).then(
      () => {
        this.employee.teamIds = angular.copy(teamIds);
        this.$rootScope.$broadcast("employeesChanged");
        this.events.broadcastEmployeeUpdated(this.employee);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  openAvatarSelector = (): void => {
    this.changeUserPictureModalService.openChangeUserPictureModal(this.employee.id);
  };

  private getComponentData = (): IPromise<void> => {
    const userId = this.$stateParams.employeeId;
    const userPromise: IPromise<IUser> = this.userService.getUser(userId);

    return this.$q
      .all({
        user: userPromise,
        employee: this.getEmployee(),
      })
      .then(({ user }) => {
        this.user = user;
        this.user.customFields = this.user.customFields || {};
        this.isCurrentUserProfile = this.user.email === this.profile.email;
      });
  };

  private bindStateToScope = (state: IAssigneesStoreState) => {
    return {
      teamsIdMap: toTeamAssigneeIdMap(state),
    };
  };

  private getEmployee = (): IPromise<void> => {
    const mongoQuery = {
      filter: {
        _id: this.$stateParams.employeeId,
      },
      fields: "badges,email,firstName,lastName,id,managedTeamIds,picture,teamIds,customFields,dateCreated,isActive",
    };

    return this.employeeService.getEmployeesV2(mongoQuery).then(
      (employeesCollection) => {
        // always array of one element because of the id filter
        const employee = employeesCollection.items[0];
        this.allTeamIds = unique([...employee.managedTeamIds, ...employee.teamIds]);
        this.employee = employee as IEnrichedEmployee;
        this.employee.type = "user";
        this.indicators.loading = false;
        this.events.broadcastEmployeeVisited(employee);
      },
      (error) => {
        this.indicators.loading = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  public getNavItem = (): INavItem => {
    if (!this.user) return undefined;
    return this.favoritesManager.getFavoriteItem({ id: this.user.id }) || navItemsFactory.buildEmployeeNavItem(this.user);
  };

  public employeeIsFavorite = (): boolean => {
    if (!this.user) return false;
    return this.favoritesManager.entityIsFavorite({ id: this.user.id });
  };

  public isCustomFieldDisabled = (): boolean => {
    return !hasPermissions(this.$ngRedux.getState<IPermissionsStoreState>(), "ManagePeople");
  };

  public updateCustomFields = (customFields: CfMap): void => {
    if (areCustomFieldsChanged(customFields, this.user.customFields)) {
      const partialEmployee: Partial<IUser> = { customFields: customFields };
      this.userService.patchUser(this.employee.id, partialEmployee).then(
        () => {
          this.$rootScope.$broadcast("employeesChanged");
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    }
  };
}

export const EmployeeProfileComponent: IComponentOptions = {
  controller: EmployeeProfileCtrl,
  bindings: {
    dismiss: "&",
    close: "&",
  },
  template: require("../employee-profile/employee-profile.component.html"),
};
